/**
 * ValidatorNameBadge — Lane V.2 (WP #4084).
 *
 * Renders a validator name with a small badge indicating naming provenance:
 *
 *   • Verified  — registered / signed (Config program or Keybase)
 *   • Inferred  — heuristic (provider + country + stake rank)
 *   • Unverified — no registered name; pubkey-only
 *
 * Accessibility:
 *   - Each tier carries an `aria-label` separate from any decorative icon.
 *   - Tier is NOT communicated by colour alone — every badge has a textual /
 *     iconographic cue (shield, dotted underline, mono pubkey).
 *   - Colours meet WCAG AA contrast against both light and dark surfaces in
 *     the existing Tailwind palette (success-500 / warning-500 / secondary-500
 *     used as token semantics, not raw hex).
 *
 * Consumes `formatValidatorLabel(...)` so every surface renders the same
 * label + tooltip text.
 */

import React from 'react';
import { ShieldCheck, Info, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  formatValidatorLabel,
  type NameTier,
  type ValidatorLabel,
} from '@/utils/validatorName';

export interface ValidatorNameBadgeProps {
  /** Validator record fields. Tolerates partial shape. */
  validator: {
    name?: string | null;
    pubkey?: string | null;
    nodePubkey?: string | null;
    nameProvenance?: unknown;
  };
  /** Optional pre-computed label (skip re-running formatter). */
  label?: ValidatorLabel;
  /** If set, wrap the name in a <Link> to the validator detail page. */
  linkTo?: string;
  /** Optional CSS class on the outer wrapper. */
  className?: string;
  /** If true, hide the small tier badge — render text + tooltip only. */
  hideBadge?: boolean;
  /** Size variant — `sm` for table cells, `md` for headers. */
  size?: 'sm' | 'md';
  /** Click-handler passthrough. */
  onClick?: (e: React.MouseEvent) => void;
}

interface TierVisual {
  icon: React.ComponentType<{ className?: string }> | null;
  iconLabel: string;
  textClasses: string;
  badgeClasses: string;
  ariaLabel: string;
}

function tierVisuals(tier: NameTier): TierVisual {
  switch (tier) {
    case 'verified':
      return {
        icon: ShieldCheck,
        iconLabel: 'Verified validator name',
        textClasses: 'text-gray-900 dark:text-white',
        badgeClasses:
          'bg-success-100 text-success-700 border border-success-300 ' +
          'dark:bg-success-500/15 dark:text-success-300 dark:border-success-500/40',
        ariaLabel:
          'Verified — name from registered, signed source (Config program or Keybase).',
      };
    case 'inferred':
      return {
        icon: Info,
        iconLabel: 'Inferred validator name',
        // Italic + dotted underline carries the cue without relying on colour.
        textClasses:
          'italic text-gray-700 dark:text-gray-200 underline decoration-dotted ' +
          'underline-offset-4 decoration-warning-500/70',
        badgeClasses:
          'bg-warning-100 text-warning-700 border border-warning-300 ' +
          'dark:bg-warning-500/15 dark:text-warning-300 dark:border-warning-500/40',
        ariaLabel:
          'Inferred — name derived from gossip / provider + country metadata. ' +
          'Lower confidence; this validator has not published on-chain Config metadata.',
      };
    case 'unverified':
      return {
        icon: HelpCircle,
        iconLabel: 'Unverified validator (no registered name)',
        // Mono font signals "this is a raw address, not a name".
        textClasses:
          'font-mono text-gray-600 dark:text-gray-300',
        badgeClasses:
          'bg-gray-100 text-gray-600 border border-gray-300 ' +
          'dark:bg-gray-700/40 dark:text-gray-300 dark:border-gray-600',
        ariaLabel:
          'Unverified — no registered name. Showing truncated public key.',
      };
  }
}

const TIER_LABEL: Record<NameTier, string> = {
  verified: 'Verified',
  inferred: 'Inferred',
  unverified: 'Unverified',
};

export const ValidatorNameBadge: React.FC<ValidatorNameBadgeProps> = ({
  validator,
  label,
  linkTo,
  className = '',
  hideBadge = false,
  size = 'sm',
  onClick,
}) => {
  const resolved = label ?? formatValidatorLabel(validator);
  const visual = tierVisuals(resolved.tier);
  const Icon = visual.icon;

  const textSizeClass = size === 'md' ? 'text-sm md:text-base' : 'text-xs md:text-sm';
  const badgeSizeClass =
    size === 'md'
      ? 'text-[10px] md:text-xs px-2 py-0.5'
      : 'text-[10px] px-1.5 py-0.5';

  const nameElement = linkTo ? (
    <Link
      to={linkTo}
      onClick={onClick}
      className={`${visual.textClasses} ${textSizeClass} hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate`}
      aria-label={visual.ariaLabel}
      title={resolved.tooltip}
    >
      {resolved.text}
    </Link>
  ) : (
    <span
      className={`${visual.textClasses} ${textSizeClass} truncate`}
      aria-label={visual.ariaLabel}
      title={resolved.tooltip}
    >
      {resolved.text}
    </span>
  );

  return (
    <span
      className={`inline-flex items-center gap-1.5 min-w-0 ${className}`}
      data-name-tier={resolved.tier}
      data-name-provenance={resolved.provenance ?? 'unknown'}
    >
      {nameElement}
      {!hideBadge && (
        <span
          className={`inline-flex items-center gap-0.5 rounded-full font-medium uppercase tracking-wide flex-shrink-0 ${visual.badgeClasses} ${badgeSizeClass}`}
          role="img"
          aria-label={visual.ariaLabel}
          title={resolved.tooltip}
        >
          {Icon && <Icon className="w-3 h-3" aria-hidden="true" />}
          <span>{TIER_LABEL[resolved.tier]}</span>
        </span>
      )}
    </span>
  );
};

export default ValidatorNameBadge;
